import { useState } from 'react';
import { Button, Modal } from '@examflow/ui';

export type FeedbackType = 'wrong-answer' | 'unclear' | 'typo' | 'other';

export interface QuestionFeedbackData {
  questionId: string;
  type: FeedbackType;
  description?: string;
}

interface QuestionFeedbackModalProps {
  questionId: string;
  onClose: () => void;
  onSubmit: (data: QuestionFeedbackData) => Promise<void>;
}

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'wrong-answer', label: '答案错误' },
  { value: 'unclear', label: '题目表述不清' },
  { value: 'typo', label: '错别字/排版问题' },
  { value: 'other', label: '其他问题' },
];

export function QuestionFeedbackModal({
  questionId,
  onClose,
  onSubmit,
}: QuestionFeedbackModalProps) {
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selectedType) {
      setError('请选择问题类型');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        questionId,
        type: selectedType,
        description: description.trim() || undefined,
      });
      onClose();
    } catch {
      setError('提交失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={isSubmitting ? () => undefined : onClose}
      title="报告问题"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            取消
          </Button>
          <Button onClick={() => void handleSubmit()} isLoading={isSubmitting}>
            提交反馈
          </Button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-500">
        帮助我们改进题目质量。请选择问题类型并描述您发现的问题。
      </p>

      <div className="mb-4 space-y-2">
        {FEEDBACK_TYPES.map((ft) => (
          <label
            key={ft.value}
            className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
              selectedType === ft.value
                ? 'border-primary-300 bg-primary-50'
                : 'border-slate-200 hover:bg-slate-50'
            }`}
          >
            <input
              type="radio"
              name="feedback-type"
              value={ft.value}
              checked={selectedType === ft.value}
              onChange={() => setSelectedType(ft.value)}
              className="h-4 w-4 text-primary-600"
            />
            <span className="text-sm font-medium text-slate-700">{ft.label}</span>
          </label>
        ))}
      </div>

      <div className="mb-2">
        <label
          htmlFor="feedback-description"
          className="mb-1.5 block text-sm font-medium text-slate-700"
        >
          详细描述（可选）
        </label>
        <textarea
          id="feedback-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="请描述您发现的具体问题..."
          rows={3}
          className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </Modal>
  );
}
